import { useTranslation } from "react-i18next"

import { ImageSize } from "@/features/studio-ai/types/studio-ai.types"
import { cn } from "@/lib/utils"

type ImageFormatStepProps = {
  selected: ImageSize
  onSelect: (size: ImageSize) => void
}

const FORMAT_OPTIONS = [
  {
    value: ImageSize.Square1024,
    labelKey: "square",
    descKey: "squareDesc",
    aspectClass: "aspect-square w-16",
  },
  {
    value: ImageSize.Landscape1536,
    labelKey: "landscape",
    descKey: "landscapeDesc",
    aspectClass: "aspect-[3/2] w-20",
  },
  {
    value: ImageSize.Portrait1536,
    labelKey: "portrait",
    descKey: "portraitDesc",
    aspectClass: "aspect-[2/3] w-12",
  },
] as const

export function ImageFormatStep({ selected, onSelect }: ImageFormatStepProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.image.format" })

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t("title")}</h2>
        <p className="text-muted-foreground text-sm">{t("description")}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        {FORMAT_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            className={cn(
              "flex flex-col items-center gap-3 rounded-xl border-2 p-6 transition-[border-color,background-color,box-shadow]",
              "hover:border-primary/50 hover:shadow-sm",
              "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
              selected === option.value ? "border-primary bg-primary/5 shadow-sm" : "border-border"
            )}
            onClick={() => onSelect(option.value)}
          >
            <div
              className={cn(
                "bg-muted rounded-lg border-2",
                option.aspectClass,
                selected === option.value ? "border-primary" : "border-muted-foreground/30"
              )}
            />
            <div className="text-center">
              <p className="font-medium">{t(option.labelKey)}</p>
              <p className="text-muted-foreground text-xs">{t(option.descKey)}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}
